import React, { Component } from 'react';
import { cloneDeep, debounce } from 'lodash';
import moment from 'moment';

import { getClass } from '../../../utils/component-helpers';
import { dateService } from '../../../services/date';
import smartlinkService from '../../admin/smartlinks/smartlink-service';

import BasicDropdown from '../dropdowns/basic-dropdown';
import Tooltip from '../../misc/tooltip';

import './auction-override.scss';
import smallX from 'images/icons/X-small.png';


class AuctionOverride extends Component {
	constructor(props) {
		super(props);

		this.SmartlinkService = smartlinkService;
		this.DateService = dateService;
		this.defaultStockStatusSet = false;
		this.highlighted = false;
		this.inputNode = null; // Used for price override focus

		const errorMap = {
			invalidDateFormat: 'Please enter a valid date format mm/dd/yyyy',
			futureDate: 'Expiration must be a future date',
			withinYear: 'Expiration must be within the next year',
			empty: 'Expiration must be provided with override',
		};

		const overrideMaps = {
			retailerPrice: {
				type: 'price',
				auctionConfig: 'sale_price',
				regex: this.SmartlinkService.priceRegex,
				valueErrorKey: 'price',
				expirationErrorKey: 'priceExpiration',
				labels: {
					overrideValue: 'Override Price',
					showOverride: 'Add Price Override',
				},
				errors: {
					invalidPriceFormat: 'Please enter a valid price format $xx.xx',
					...errorMap,
				},
			},
			stockStatus: {
				type: 'stock',
				auctionConfig: 'is_in_stock',
				expirationErrorKey: 'stockExpiration',
				fields: this.SmartlinkService.getDefaultStockFields(this.props.product.is_in_stock),
				labels: {
					overrideValue: 'Override Stock Status',
					showOverride: 'Add Stock Override',
				},
				errors: {
					...errorMap,
				},
			},
		};

		this.overrideMap = overrideMaps[this.props.type];

		const auctionConfig = this.props.product.auction_config[this.overrideMap.auctionConfig];

		// Stock Specific
		let dropdownFields, overrideStockFields;

		if (this.overrideMap.fields) {
			dropdownFields = cloneDeep(this.overrideMap.fields).splice(1, 2);
			if (auctionConfig.value !== null) {
				// Stock overrides should only have two options- in stock and out of stock
				overrideStockFields = this.SmartlinkService.getDefaultStockFields(
					auctionConfig.value, dropdownFields
				);
			}
		}

		this.state = {
			// Toggle override
			showOverride: auctionConfig.value !== null,

			// Price Specific
			valueOverride: auctionConfig.value ? parseFloat(auctionConfig.value).toFixed(2) : '',

			// Stock Specific
			overrideStock: auctionConfig.value,
			statusLabel: overrideStockFields ? this.SmartlinkService.getLabel(overrideStockFields) : null,
			stockStatusFields: dropdownFields || null,

			// Expiration inputs
			expirationOverride: auctionConfig.ttl_expiration ?
				this.DateService.formatDate(auctionConfig.ttl_expiration, 'MM/DD/YY') : '',
			expirationPresets: cloneDeep(this.SmartlinkService.expirationPresets),

			productIdxWithError: this.props.productIdxWithError,
			errorMsg: null,
			dropdownOpened: false,
		};

		/*
		 * One second debounce on updating overrides so we're not constantly
		 * updating the product.
		 */
		this.debounceUpdateProduct = debounce((product) => {
			this.props.updateProduct(product, this.props.productIndex);
		}, 1000);
	}

	toggleOverride() {
		this.setState({showOverride: true}, () => {
			if (this.inputNode) {
				this.inputNode.focus();
			}
		});
		this.selectExpirationPreset(1);

		if (this.overrideMap.type === 'stock' && !this.defaultStockStatusSet) {
			const currentStatus = this.state.stockStatusFields.find((item) => item.selected);
			const newStatusValue = !currentStatus.value;
			const newIndex = this.state.stockStatusFields.findIndex((item) => item.value === newStatusValue);
			this.defaultStockStatusSet = true;

			this.selectStockStatus(newIndex);
		}

		this.props.toggleOverrideOpen(this.overrideMap.type);
	}

	// Helper Functions
	formatOverride() {
		if (this.overrideMap.type === 'price') {
			return parseFloat(this.state.valueOverride).toFixed(2).toString();
		}
	}

	addTooltip(el) {
		return new Tooltip(el, {text: 'Remove Override', size: 2});
	}

	addError(indices, type) {
		indices.push(this.props.productIndex);
		this.props.setError(true, this.props.productIndex, type, 'add');
	}

	// Validation Functions
	validateDate() {
		const expirationErrorKey = this.overrideMap.expirationErrorKey;
		const currentDate = this.DateService.getCurrentDateTime();
		const override = moment(this.state.expirationOverride);
		let indices = cloneDeep(this.state.productIdxWithError[expirationErrorKey]);
		let errorMsg = null;

		if (!override.isValid() && this.state.expirationOverride !== '') {
			// Check for valid date format
			errorMsg = this.overrideMap.errors.invalidDateFormat;
			this.addError(indices, expirationErrorKey);
		} else if (override < currentDate) {
			// Check if expiration date is a past date
			errorMsg = this.overrideMap.errors.futureDate;
			this.addError(indices, expirationErrorKey);
		} else if (override > this.DateService.offsetFromDate(currentDate, 365, 'day')) {
			// Check if expiration date is within a year
			errorMsg = this.overrideMap.errors.withinYear;
			this.addError(indices, expirationErrorKey);
		} else if (!this.state.expirationOverride && this.state.showOverride) {
			errorMsg = this.overrideMap.errors.empty;
			this.addError(indices, expirationErrorKey);
		} else {
			indices = indices.filter((item) => item !== this.props.productIndex);
			this.props.setError(false, this.props.productIndex, expirationErrorKey, 'remove');
		}

		this.setState({
			errorMsg: errorMsg,
			productIdxWithError: {
				...this.state.productIdxWithError,
				[expirationErrorKey]: indices,
			},
		});
	}

	validateOverride() {
		const override = this.state.valueOverride ? this.formatOverride() : '';
		let indices = cloneDeep(this.state.productIdxWithError[this.overrideMap.type]);
		let errorMsg = null;

		if (!override || (override && !override.match(this.overrideMap.regex))) {
			errorMsg = this.overrideMap.errors.invalidPriceFormat;
			indices.push(this.props.productIndex);
			this.props.setError(true, this.props.productIndex, this.overrideMap.type, 'add');
		} else {
			indices = indices.filter((item) => item !== this.props.productIndex);
			this.props.setError(false, this.props.productIndex, this.overrideMap.type, 'remove');
		}

		this.setState({
			valueOverride: override,
			errorMsg: errorMsg,
			productIdxWithError: {
				...this.state.productIdxWithError,
				[this.overrideMap.type]: indices,
			},
		}, () => {
			// Just show error first if invalid
			if (errorMsg) return;
			this.validateDate();
		});
	}

	// Update Functions
	updateOverride(event) {
		this.setState({
			valueOverride: event.target.value,
		}, () => {
			// Update product
			const valueOverride = this.state.valueOverride ? this.formatOverride() : '';
			const product = cloneDeep(this.props.product);
			product.auction_config[this.overrideMap.auctionConfig].value = valueOverride;

			this.debounceUpdateProduct(product);
		});
	}

	updateExpiration(event) {
		this.setState({
			expirationOverride: event.target.value,
		}, () => {
			// Update product
			const expirationOverride = cloneDeep(this.state.expirationOverride);
			const product = cloneDeep(this.props.product);
			product.auction_config[this.overrideMap.auctionConfig].ttl_expiration = expirationOverride;

			this.debounceUpdateProduct(product);
		});
	}

	removeOverride() {
		this.highlighted = false;
		const product = cloneDeep(this.props.product);
		const productIdxWithError = cloneDeep(this.state.productIdxWithError);
		const errors = {};

		this.props.toggleOverride(this.overrideMap.type);
		this.props.toggleOverrideOpen(this.overrideMap.type);

		this.setState({
			showOverride: null,
			overrideStock: null,
			valueOverride: '',
			expirationOverride: '',
		}, () => {
			product.auction_config[this.overrideMap.auctionConfig] = {
				value: null,
				ttl_expiration: null,
			};
		});

		const keys = [this.overrideMap.expirationErrorKey];
		if (this.overrideMap.type === 'price') keys.push(this.overrideMap.valueErrorKey);

		keys.forEach((key) => {
			if (productIdxWithError[key].includes(this.props.productIndex)) {
				this.props.setError(false, this.props.productIndex, key, 'remove');
				errors[key] = productIdxWithError[key].filter((item) => item !== this.props.productIndex);
			}
		});

		if (errors) {
			this.setState({
				productIdxWithError: {
					...productIdxWithError,
					...errors,
				},
				errorMsg: null,
			});
		}

		this.props.updateProduct(product, this.props.productIndex);
	}

	// Prop Functions
	selectExpirationPreset(index) {
		const expirationPresets = this.state.expirationPresets;

		// Reset all items
		expirationPresets.map((preset) => preset.selected = false);
		expirationPresets[index].selected = true;

		this.setState({
			expirationOverride: expirationPresets[index].value,
		}, () => {
			this.validateDate();

			// Update product
			const expirationOverride = cloneDeep(this.state.expirationOverride);
			const product = cloneDeep(this.props.product);
			product.auction_config[this.overrideMap.auctionConfig].ttl_expiration = expirationOverride;

			this.props.updateProduct(product, this.props.productIndex);
		});
	}

	selectStockStatus(index) {
		const stockStatuses = cloneDeep(this.state.stockStatusFields);

		// Reset all items
		stockStatuses.map((status) => status.selected = false);
		stockStatuses[index].selected = true;

		// Update product
		const product = cloneDeep(this.props.product);
		product.auction_config.is_in_stock.value = stockStatuses[index].value;

		this.props.updateProduct(product, this.props.productIndex);

		this.setState({
			overrideStock: stockStatuses[index].value,
			stockStatusFields: stockStatuses,
			statusLabel: stockStatuses[index].label,
		}, () => {
			this.validateDate();
		});
	}

	highlightText(e) {
		if (!this.highlighted) {
			this.highlighted = true;
			const node = e.target;

			if (document.body.createTextRange) { // IE highlight implementation
				const range = document.body.createTextRange();
				range.moveToElementText(node);
				range.select();
			} else if (window.getSelection) { // Others
				const range = document.createRange();
				node.focus();
				range.selectNode(node);
				window.getSelection().removeAllRanges();
				window.getSelection().addRange(range);
			}

			// JS click since dropdown state is not reachable from parent
			const dropdown = document.querySelector(
				`.${this.overrideMap.type}-override-expiration .basic-dropdown`
			);
			dropdown.click();
		}
	}

	handleDropdown(opened) {
		this.setState({dropdownOpened: opened});
	}

	render() {
		const type = this.overrideMap.type;
		const valueErrors = this.state.productIdxWithError[this.overrideMap.valueErrorKey];
		const expirationErrors = this.state.productIdxWithError[this.overrideMap.expirationErrorKey];

		const hasValueErr = valueErrors ? valueErrors.includes(this.props.productIndex) : null;
		const hasExpirationErr = expirationErrors ? expirationErrors.includes(this.props.productIndex) : null;

		const overrideValueTemplate = type === 'price' ? (
			<input
				// eslint-disable-next-line
				ref={(node) => this.inputNode = node}
				type="number"
				placeholder={this.state.valueOverride}
				value={this.state.valueOverride}
				className={getClass(`${type}-override-input`, {'error': hasValueErr})}
				onChange={(e) => this.updateOverride(e)}
				onBlur={(e) => this.validateOverride(e)}
			/>
		) : (
			<div className={`stock-dropdown-container ${this.state.dropdownOpened ? 'active' : ''}`}>
				<BasicDropdown
					items={this.state.stockStatusFields}
					selectItem={this.selectStockStatus.bind(this)}
					dropdownLabel={this.state.statusLabel}
					handleDropdown={this.handleDropdown.bind(this)}
				/>
			</div>
		);

		const override = this.state.showOverride ? (
			<div className={`existing-${type}-override`}>
				<div className="flex-box">
					<div className="left-side">
						<div className="label">
							{this.overrideMap.labels.overrideValue}
						</div>
						<div className={`${type}-override-container`}>
							{overrideValueTemplate}
						</div>
					</div>
					<div className="right-side">
						<div className="label">
							Expiration
						</div>
						<div className={`${type}-override-expiration`}>
							<input
								type="text"
								className={getClass('override-expiration-input', {'error': hasExpirationErr})}
								placeholder={this.state.expirationOverride}
								value={this.state.expirationOverride}
								onChange={(e) => this.updateExpiration(e)}
								onClick={(e) => this.highlightText(e)}
								onBlur={() => this.validateDate()}
							/>
							<BasicDropdown
								items={this.state.expirationPresets}
								parentContain={true}
								selectItem={(idx) => this.selectExpirationPreset(idx)}
								dropdownLabel=""
							/>
						</div>
					</div>
					<div
						className="remove-override-container"
						ref={(node) => this.addTooltip(node)}
						onClick={(e) => this.removeOverride(e)}>
						<img
							src={smallX}
							alt="remove"
							className={`remove-override ${type}`}
						/>
					</div>
				</div>
				<div className={`error-msg ${this.overrideMap.type}`}>
					{this.state.errorMsg}
				</div>
			</div>
		) : (
			<div className={`${type}-override-btn-container`}>
				<button
					className={`${type}-override-btn`}
					onClick={() => this.toggleOverride()}>
					{this.overrideMap.labels.showOverride}
				</button>
			</div>
		);

		return (
			<div className={`overrides-container ${type}`}>
				{override}
			</div>
		);
	}
}

export default AuctionOverride;
